/**
 * Pure helpers behind Dropdown — normalising, grouping and filtering options.
 *
 * Kept out of the component so the fiddly bits (two option shapes, groups,
 * search matching, select-all state) are unit-testable without rendering.
 *
 * The legacy app accepts two option shapes and the port must take both:
 *   - a plain string array, where label and value are the same string
 *     (`:items="providers"` — the dominant shape); and
 *   - objects with item-title/item-value, where the label differs from the
 *     bound value (`item-title="title" item-value="id"`).
 * Note `item-text` also appears in the legacy templates — it is a Vuetify 2
 * prop that Vuetify 3 ignores, so it is dead there and not supported here.
 */

/**
 * Coerce one option into { value, label, icon, disabled }.
 * @param {string | {value?:any, title?:string, label?:string, icon?:any, disabled?:boolean}} option
 */
export function normalizeOption(option) {
  if (option == null) return null;
  if (typeof option === 'string' || typeof option === 'number') {
    return { value: option, label: String(option), icon: undefined, disabled: false };
  }
  // `title` is the legacy key; `label` is the idiomatic one. Accept both.
  const label = option.label ?? option.title ?? String(option.value ?? '');
  return {
    value: option.value !== undefined ? option.value : label,
    label,
    icon: option.icon,
    disabled: Boolean(option.disabled),
  };
}

/** Normalise a flat option list. Nulls are dropped rather than rendered blank. */
export function normalizeOptions(options = []) {
  return options.map(normalizeOption).filter(Boolean);
}

/**
 * Normalise `groups` into [{ label, options: [...] }].
 * A flat `options` array becomes a single unlabelled group, so the renderer
 * only ever deals with one shape.
 */
export function normalizeGroups({ options, groups }) {
  if (groups?.length) {
    return groups.map((g) => ({ label: g.label, options: normalizeOptions(g.options) }));
  }
  return [{ label: undefined, options: normalizeOptions(options) }];
}

/** Case-insensitive substring match, the same behaviour v-autocomplete gives. */
export function matchesQuery(option, query) {
  if (!query) return true;
  return option.label.toLowerCase().includes(query.trim().toLowerCase());
}

/** Filter groups by query, dropping groups left empty. */
export function filterGroups(groups, query) {
  if (!query) return groups;
  return groups
    .map((g) => ({ ...g, options: g.options.filter((o) => matchesQuery(o, query)) }))
    .filter((g) => g.options.length > 0);
}

/** Every option across all groups, in render order — the keyboard nav sequence. */
export function flattenGroups(groups) {
  return groups.flatMap((g) => g.options);
}

/** Is this option currently selected? Handles both single and multiple. */
export function isSelected(value, optionValue, multiple) {
  if (multiple) return Array.isArray(value) && value.includes(optionValue);
  return value === optionValue;
}

/**
 * The value after clicking `optionValue`.
 * Single: replace. Multiple: toggle membership, preserving order.
 */
export function nextValue(value, optionValue, multiple) {
  if (!multiple) return optionValue;
  const current = Array.isArray(value) ? value : [];
  return current.includes(optionValue)
    ? current.filter((v) => v !== optionValue)
    : [...current, optionValue];
}

/**
 * Select-all state over the selectable (non-disabled) options.
 * Mirrors the legacy Select All checkbox, which compared
 * `selected.length === options.length`.
 * @returns {{ checked: boolean, indeterminate: boolean, allValues: any[] }}
 */
export function selectAllState(allOptions, value) {
  const selectable = allOptions.filter((o) => !o.disabled).map((o) => o.value);
  const current = Array.isArray(value) ? value : [];
  const hits = selectable.filter((v) => current.includes(v)).length;
  return {
    checked: selectable.length > 0 && hits === selectable.length,
    indeterminate: hits > 0 && hits < selectable.length,
    allValues: selectable,
  };
}

/**
 * The closed-state text. Single: the matching option's label (falling back to
 * the raw value, so a value with no matching option still shows). Multiple:
 * the legacy overflow format — first label plus "(+N others)".
 */
export function displayLabel(groups, value, multiple, placeholder = '') {
  const all = flattenGroups(groups);
  if (multiple) {
    const picked = (Array.isArray(value) ? value : []).map(
      (v) => all.find((o) => o.value === v)?.label ?? String(v),
    );
    if (picked.length === 0) return placeholder;
    if (picked.length === 1) return picked[0];
    return `${picked[0]} (+${picked.length - 1} others)`;
  }
  if (value === undefined || value === null || value === '') return placeholder;
  return all.find((o) => o.value === value)?.label ?? String(value);
}
